/**
 * useArTableExportActions Hook
 *
 * Builds the export & import menu items for ArTable menus.
 * Modal open state lives on the per-table Valtio proxy (`uiState.exportModals`);
 * menu items flip those flags and the single `<ArTableExportModals />` host
 * renders the matching modal.
 *
 * The layout element's `columnConfig` reaches `useArTableExport` and
 * `downloadTemplateCsv` raw: `undefined` is every schema column, `[]` is none.
 * Never default it to `[]` on the way through (SYD-2279).
 */

import { useCallback, useContext, useMemo } from 'react';
import { MailOutlined, FilePdfOutlined, FileExcelOutlined, FileTextOutlined, UploadOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { snapshot } from 'valtio';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import { useStoreNonReactive } from '@/store/useStoreNonReactive';
import { useCompositeFieldTypes } from '@/components/CompositeField/useCompositeFieldTypes';
import { ArTableViewSortContext, ArTableUIStateContext } from '../context';
import { resolveArTableRowsOrderBy } from '../resolveArTableRowsOrderBy';
import { useArTableExport } from './useArTableExport';
import { useFormXlsxExport } from './useFormXlsxExport';
import { downloadTemplateCsv } from '../import';
import type { ArTableId, ArTableType } from '#types/System.Artifacts.types';
import type { UIArTableColumnOverride } from '#types/System.types';
import type { DocumentState } from '@/store/Store.types';
import type { Snapshot } from 'valtio';

type UseArTableExportActionsProps = {
	tableId: ArTableId;
	parentPath: string;
	disabled?: boolean;
	columnConfig?: UIArTableColumnOverride[];
};

type UseArTableExportActionsReturn = {
	exportMenuItems: MenuProps['items'];
	exporting: boolean;
};

function useArTableExportActions({
	tableId,
	parentPath,
	disabled = false,
	columnConfig,
}: UseArTableExportActionsProps): UseArTableExportActionsReturn {
	const { fieldTypes } = useContext(FieldTypeContext);
	const { state: schemaState } = useStoreNonReactive('schema');
	const viewSortCtx = useContext(ArTableViewSortContext);
	const uiState = useContext(ArTableUIStateContext)!;
	const compositeFieldTypes = useCompositeFieldTypes();

	const rowsOrderBy = useMemo(() => {
		const viewSort = viewSortCtx?.viewSort;
		return resolveArTableRowsOrderBy(viewSort);
	}, [viewSortCtx?.viewSort]);

	const { handleExportXlsx, exportingXlsx } = useArTableExport({ tableId, parentPath, rowsOrderBy, columnConfig });
	const { handleExportFormXlsx, exportingFormXlsx } = useFormXlsxExport();

	const exporting = exportingXlsx || exportingFormXlsx;

	const handleDownloadTemplate = useCallback(() => {
		const schemaSnap = snapshot(schemaState) as Snapshot<DocumentState<ArTableType>>;
		const schema = schemaSnap.data as ArTableType | undefined;
		if (!schema) return;
		downloadTemplateCsv(schema, fieldTypes, columnConfig, compositeFieldTypes);
	}, [schemaState, fieldTypes, columnConfig, compositeFieldTypes]);

	const exportMenuItems: MenuProps['items'] = useMemo(() => [
		{
			key: 'export-share',
			type: 'group' as const,
			label: 'Export & Share',
			children: [
				{
					key: 'send-email',
					label: 'Send Via Email',
					icon: <MailOutlined />,
					disabled: disabled || exporting,
					onClick: () => { uiState.exportModals.email = true; },
				},
				{
					key: 'export-pdf',
					label: 'Export Form as PDF',
					icon: <FilePdfOutlined />,
					disabled: disabled || exporting,
					onClick: () => { uiState.exportModals.pdf = true; },
				},
				{
					key: 'export-form-xlsx',
					label: 'Export Form as XLSX',
					icon: <FileExcelOutlined />,
					disabled: disabled || exporting,
					onClick: () => { void handleExportFormXlsx(); },
				},
				{
					key: 'export-table-xlsx',
					label: 'Export This Table as XLSX',
					icon: <FileExcelOutlined />,
					disabled: disabled || exporting,
					onClick: () => { void handleExportXlsx(); },
				},
			],
		},
		{
			key: 'import',
			type: 'group' as const,
			label: 'Import',
			children: [
				{
					key: 'download-csv-template',
					label: 'Download Template (CSV)',
					icon: <FileTextOutlined />,
					disabled: disabled || exporting,
					onClick: handleDownloadTemplate,
				},
				{
					key: 'import-csv',
					label: 'Import Data (CSV)',
					icon: <UploadOutlined />,
					disabled: disabled || exporting,
					onClick: () => { uiState.exportModals.import = true; },
				},
			],
		},
	], [disabled, exporting, handleDownloadTemplate, handleExportFormXlsx, handleExportXlsx, uiState]);

	return { exportMenuItems, exporting };
}

export { useArTableExportActions };
export type { UseArTableExportActionsReturn };
